import React, { useState, useEffect } from "react";
import { Button, Container, Row, Col, Table, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import { useNavigate } from "react-router-dom";

function ActivePhoneNumbers() {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isPilot, setIsPilot] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const getNextChargeDate = (num) => {
    const baseDate = num.lastBilledAt || num.createdAt;
    if (!baseDate) return "N/A";
    const date = new Date(baseDate);
    date.setDate(date.getDate() + 30);
    return date.toLocaleDateString();
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchNumbers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/phone-numbers/list`, {
        headers: getAuthHeaders(),
      });
      if (res.data.success) {
        setPhoneNumbers(res.data.phoneNumbers);
        setIsAdmin(res.data.isAdmin || false);
        setIsPilot(res.data.pilot === true);
      }
    } catch (error) {
      console.error("Error fetching numbers", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNumbers();
  }, []);

  const syncNumbers = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/phone-numbers/update-from-twilio`, {}, { headers: getAuthHeaders() });
      if (res.data.success) {
        Swal.fire("Synchronized", res.data.message, "success");
        fetchNumbers();
      }
    } catch (error) {
      Swal.fire("Sync Failed", error.response?.data?.message || "Failed to sync numbers", "error");
    } finally {
      setSyncing(false);
    }
  };

  const deleteNumber = async (id) => {
    let impact;
    try {
      const preview = await axios.get(`${API_BASE_URL}/phone-numbers/${id}/release-impact`, { headers: getAuthHeaders() });
      impact = preview.data.impact;
      if (!preview.data.success || !impact) throw new Error("Missing release preview");
    } catch (error) {
      Swal.fire("Preview unavailable", error.response?.data?.message || "Could not check the release impact. Please retry.", "error");
      return;
    }
    const confirm = await Swal.fire({
      title: "Permanently release this number?",
      text: `This returns ${impact.phoneNumber} to Twilio. New messages to it will no longer reach this app. ` +
        `${impact.affectedConversations} conversation(s) reference this number; replies from the currently selected sender will be disabled in ${impact.selectedSenderBlocked}. ` +
        `${impact.assignedCampaigns} assigned campaign(s), ${impact.pendingCampaignSends} queued campaign send(s), and ${impact.pendingReplies} pending reply/replies are affected. ` +
        `${impact.conversationsWithOtherLegs} conversation(s) also have other number legs; those remain available without automatic rerouting. ` +
        `History is preserved. Use Deactivate to pause this number without permanently releasing it. Counts cover recorded Conversations history; older history may need migration. ` +
        (impact.unresolvedSends ? `${impact.unresolvedSends} unresolved send(s) must be reconciled before release can complete. Starting release blocks new sends.` : ""),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: impact.unresolvedSends ? "Block sends and pause release" : "Yes, release number",
      customClass: {
        confirmButton: "btn btn-danger bg-danger fw-bold text-white px-4 py-2",
        cancelButton: "btn btn-secondary bg-secondary fw-bold px-4 py-2 ms-2 ml-2",
      },
      buttonsStyling: false,
    });

    if (confirm.isConfirmed) {
      try {
        const result = await axios.delete(`${API_BASE_URL}/phone-numbers/delete/${id}`, {
          headers: getAuthHeaders(),
        });
        if (result.data.releaseStatus === 'pending_release') fetchNumbers();
        else setPhoneNumbers((prev) => prev.filter((num) => num._id !== id));
        Swal.fire(result.data.releaseStatus === 'pending_release' ? 'Release in progress' : 'Released', result.data.message, "success");
      } catch (error) {
        Swal.fire("Release incomplete", error.response?.data?.message || "Release could not be confirmed. Refresh and retry.", "error");
        fetchNumbers();
      }
    }
  };

  const toggleActiveStatus = async (id, currentStatus) => {
    try {
      setSyncing(true); // Re-use the syncing spinner state or just disable
      const newStatus = !currentStatus;
      const res = await axios.put(`${API_BASE_URL}/phone-numbers/toggle-active/${id}`, { isActive: newStatus }, { headers: getAuthHeaders() });
      if (res.data.success) {
        setPhoneNumbers(phoneNumbers.map(num => num._id === id ? { ...num, isActive: newStatus } : num));
        Swal.fire("Success", res.data.message, "success");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to toggle status", "error");
    } finally {
      setSyncing(false);
    }
  };

  const reactivateSingleNumber = async (num) => {
    const confirm = await Swal.fire({
      title: "Reactivate Phone Number?",
      html: `Reactivating <strong>${num.phoneNumber}</strong> will cost <strong>200 credits ($2.00)</strong>.<br/><br/>Are you sure you want to proceed?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirm Reactivation",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#004992",
      cancelButtonColor: "#6c757d"
    });

    if (!confirm.isConfirmed) return;

    try {
      setSyncing(true);
      const res = await axios.post(
        `${API_BASE_URL}/billing/reactivate-numbers`,
        { phoneIds: [num._id] },
        { headers: getAuthHeaders() }
      );

      if (res.data.success) {
        Swal.fire("Reactivated", `Successfully reactivated ${num.phoneNumber}`, "success");
        fetchNumbers();
      }
    } catch (error) {
      console.error("Reactivation failed:", error);
      Swal.fire("Reactivation Failed", error.response?.data?.message || "Failed to reactivate number.", "error");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Container fluid className="p-3">
      <Row>
        <Col lg="12" sm="12">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div style={{ flex: 1, marginRight: "40px" }}>
              <h2 className="fw-bold mb-1" style={{ fontWeight: "700" }}>Active & Rotation Numbers</h2>
              <p className="text-muted mb-0" style={{ fontWeight: "600", fontSize: "0.95rem" }}>
                Manage your purchased phone numbers. Newly bought numbers are bound to your account. Toggle whether each number participates in active campaign rotation below.
              </p>
            </div>
            <div className="d-flex align-items-center flex-shrink-0" style={{ gap: "10px" }}>
              <button
                className="btn fw-bold"
                disabled={syncing || isPilot}
                onClick={syncNumbers}
                style={{
                  backgroundColor: "#111111",
                  color: "white",
                  padding: "10px 25px",
                  fontWeight: "700",
                  borderRadius: "6px",
                  border: "none",
                  whiteSpace: "nowrap"
                }}
              >
                {syncing ? "Syncing..." : "Sync Numbers"}
              </button>
              <button
                className="btn fw-bold"
                style={{
                  backgroundColor: "#004992",
                  color: "white",
                  padding: "10px 25px",
                  fontWeight: "700",
                  borderRadius: "6px",
                  border: "none",
                  whiteSpace: "nowrap"
                }}
                disabled={isPilot}
                onClick={() => navigate("/admin/buy-phone-numbers")}
              >
                Buy Phone Numbers
              </button>
            </div>
          </div>
          {isPilot && <p role="status">Pilot test number. Sync, purchase and activation are disabled. Release becomes available after messaging and billing review.</p>}
          <Table
            hover
            responsive
            style={{
              border: "1px solid #ddd",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              marginBottom: 0, background: 'white'
            }}
          >
            <thead style={{ background: 'white' }}>
              <tr style={{ display: 'flex', width: '100%', borderBottom: "1px solid #ddd" }}>
                <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2 }}>Phone Number</th>
                <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2 }}>Friendly Name</th>
                <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2 }}>Rotation Status</th>
                <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2 }}>Rent Date</th>
                <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2 }}>Next Charge</th>
                {isAdmin && <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2 }}>Assigned To</th>}
                <th style={{ fontWeight: "700", padding: "12px 10px", flex: 2, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ background: 'white', display: "block", maxHeight: "50vh", overflowY: "auto" }}>
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? "7" : "6"} className="text-center fw-bold py-4">
                    Loading numbers...
                  </td>
                </tr>
              ) : phoneNumbers.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? "7" : "6"} className="text-center fw-bold py-4 text-muted">
                    No numbers found. Click Sync Numbers to refresh your active numbers.
                  </td>
                </tr>
              ) : (
                phoneNumbers.map((num) => {
                  const daysSinceLastBill = num.lastBilledAt
                    ? (Date.now() - new Date(num.lastBilledAt).getTime()) / (1000 * 60 * 60 * 24)
                    : 30;
                  const isPastDue = num.isPastDue || (!num.isActive && daysSinceLastBill >= 29.5);

                  return (
                    <tr key={num._id} style={{ borderBottom: "1px solid #ddd", display: 'flex', width: '100%' }}>
                      <td style={{ fontWeight: "700", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center' }}>{num.phoneNumber}</td>
                      <td style={{ fontWeight: "700", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center' }}>{num.friendlyName || "N/A"}</td>
                      <td style={{ fontWeight: "700", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center' }}>
                        <span
                          className="px-2 py-1 fw-bold"
                          style={{
                            backgroundColor: num.isActive ? "#BDFFCC" : isPastDue ? "#FADBD8" : "#E0E0E0",
                            color: num.isActive ? "#009222" : isPastDue ? "#C0392B" : "#5E5E5E",
                            display: "inline-block",
                            minWidth: "120px",
                            textAlign: "center",
                            borderRadius: "8px",
                            fontSize: "12px"
                          }}
                        >
                          {num.lifecycle === "releasing" ? "Release pending" : num.isActive ? "Active Rotation" : isPastDue ? "Deactivated (Past Due)" : "Paused / Idle"}
                        </span>
                      </td>
                      <td style={{ fontWeight: "600", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center', color: "#5E5E5E" }}>
                        {num.createdAt ? new Date(num.createdAt).toLocaleDateString() : "N/A"}
                      </td>
                      <td style={{ fontWeight: "600", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center', color: "#5E5E5E" }}>
                        {num.pilot ? "Managed separately" : getNextChargeDate(num)}
                      </td>
                      {isAdmin && (
                        <td style={{ fontWeight: "700", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center' }}>
                          {num.userId && num.userId.email ? num.userId.email : "Unassigned"}
                        </td>
                      )}
                      <td style={{ fontWeight: "700", padding: "12px 10px", flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '15px' }}>
                        {num.canManage === false || num.lifecycle === "releasing" || num.isActive ? (
                          null
                        ) : isPastDue ? (
                          <span
                            className="fw-bold cursor-pointer text-warning"
                            style={{ cursor: 'pointer', fontSize: '13px' }}
                            onClick={() => reactivateSingleNumber(num)}
                          >
                            Reactivate
                          </span>
                        ) : (
                          <span
                            className="fw-bold cursor-pointer text-success"
                            style={{ cursor: 'pointer', fontSize: '13px' }}
                            onClick={() => toggleActiveStatus(num._id, num.isActive)}
                          >
                            Activate
                          </span>
                        )}
                        <button
                          type="button"
                          disabled={num.canRelease === false || (num.canRelease == null && num.canManage === false)}
                          className="fw-bold text-danger cursor-pointer"
                          style={{ cursor: 'pointer', fontSize: '13px', background: 'none', border: 0 }}
                          onClick={() => deleteNumber(num._id)}
                        >
                          {num.pilot ? (num.canRelease ? num.lifecycle === "releasing" ? "Retry release" : "Release" : "Release unavailable") : num.canManage === false ? "Owner manages release" : num.lifecycle === "releasing" ? "Retry release" : "Release"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

export default ActivePhoneNumbers;
